import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { AlertCircle, Globe, Loader2 } from 'lucide-react';
import { useTranslation, mapPublishProductError, buildProductUrl } from '@dukanti/shared';
import type { PublishProductErrorKey } from '@dukanti/shared';
import { usePublishProduct } from '@/hooks/use-publish-product';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export interface PublishProductDialogProps {
  /** Product to publish. Server re-checks admin membership on the
   *  parent store inside the RPC — client id is never trusted. */
  readonly productId: string;
  /** Slug used to build the public product URL preview. */
  readonly storeSlug: string;
  /** Controlled open state. */
  readonly open: boolean;
  /** Fires on open/close transitions from the AlertDialog primitive. */
  readonly onOpenChange: (open: boolean) => void;
  /** Product inventory shown in P0005 recovery copy. */
  readonly inventory: number;
  /** Whether the store currently has at least one category for recovery copy. */
  readonly hasCategories?: boolean;
}

/**
 * Confirmation dialog for publishing a single product (F-028).
 *
 * Pattern mirrors `PublishStoreDialog` verbatim — Globe icon in a
 * Za'atar wash circle, LTR URL pill inside an RTL dialog body, inline
 * error banner with per-branch testids, and pending-state spinner.
 *
 * Error branches are rendered one-per-`PublishProductErrorKey`, including
 * billing entitlement failures introduced by F-060.
 */
export function PublishProductDialog({
  productId,
  storeSlug,
  open,
  onOpenChange,
  inventory,
  hasCategories = true,
}: PublishProductDialogProps) {
  const { t, dir } = useTranslation();
  const publishMutation = usePublishProduct();
  const [errorKey, setErrorKey] = useState<PublishProductErrorKey | null>(null);

  const isPending = publishMutation.isPending;
  const publicUrl = buildProductUrl({ storeSlug, productId });

  useEffect(() => {
    if (!open) {
      setErrorKey(null);
    }
  }, [open]);

  function handleOpenChange(next: boolean): void {
    if (isPending) return;
    if (!next) setErrorKey(null);
    onOpenChange(next);
  }

  function handleConfirm(): void {
    if (isPending) return;
    setErrorKey(null);
    publishMutation.mutate(
      { productId },
      {
        onSuccess: () => {
          toast.success(t('admin.products.publish.toasts.published'));
          onOpenChange(false);
        },
        onError: (err) => {
          const mapped = mapPublishProductError(err);
          setErrorKey(mapped);
          toast.error(t(mapped));
        },
      },
    );
  }

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent dir={dir} data-testid="publish-product-dialog" className="gap-0 p-8">
        {/* Icon wrap — Globe in a circular Za'atar wash. */}
        <div
          aria-hidden="true"
          className="bg-zaatar-wash text-zaatar-deep mx-auto mb-4 flex size-14 items-center justify-center rounded-full"
        >
          <Globe className="size-7" strokeWidth={1.75} />
        </div>

        <AlertDialogHeader className="mb-3">
          <AlertDialogTitle className="text-center">
            {t('admin.products.publish.publishModal.title')}
          </AlertDialogTitle>
        </AlertDialogHeader>

        <AlertDialogDescription className="text-sand-600 mb-2 text-center text-sm leading-relaxed">
          {t('admin.products.publish.publishModal.description')}
        </AlertDialogDescription>

        <p className="text-sand-500 mb-2 text-center text-xs">
          {t('admin.products.publish.publishModal.urlLabel')}
        </p>

        {/* URL preview — LTR pill so `http://` renders left-to-right
            even inside an RTL dialog. */}
        <div
          data-testid="product-public-url-preview"
          dir="ltr"
          className="border-sand-200 bg-sand-100 text-zaatar-deep mb-6 truncate rounded-lg border px-4 py-3 text-center font-mono text-sm font-medium"
        >
          {publicUrl}
        </div>

        {/* Inline error banner */}
        {errorKey ? (
          <div
            role="alert"
            data-testid="publish-product-dialog-error"
            data-error-key={errorKey}
            className={cn(
              'border-terracotta/30 bg-terracotta/10 text-sand-700 rounded-card mb-6',
              'flex items-start gap-2 border p-3 text-start text-sm',
            )}
          >
            <AlertCircle
              aria-hidden="true"
              className="text-terracotta mt-0.5 size-4 shrink-0"
              strokeWidth={1.75}
            />
            <div className="flex-1">
              {errorKey === 'admin.products.publish.errors.storeNotPublished' ? (
                <>
                  <p data-testid="publish-product-dialog-error-storeNotPublished">
                    {t('admin.products.publish.errors.storeNotPublished')}
                  </p>
                  <Link
                    to="/dashboard"
                    data-testid="publish-product-dialog-storeNotPublished-cta"
                    className="text-zaatar hover:text-zaatar-deep mt-2 inline-block text-start font-medium underline"
                  >
                    {t('admin.products.publish.disabled.storeNotPublishedCta')}
                  </Link>
                </>
              ) : errorKey === 'admin.products.publish.errors.insufficientInventory' ? (
                <>
                  <p data-testid="publish-product-dialog-error-insufficientInventory">
                    {t('admin.products.publish.errors.insufficientInventory', { inventory })}
                  </p>
                  <Link
                    to={`/products/${productId}/edit`}
                    data-testid="publish-product-dialog-insufficientInventory-cta"
                    className="text-zaatar hover:text-zaatar-deep mt-2 inline-block text-start font-medium underline"
                  >
                    {t('admin.products.publish.disabled.insufficientInventoryCta')}
                  </Link>
                </>
              ) : errorKey === 'admin.products.publish.errors.categoryRequired' ? (
                <>
                  <p data-testid="publish-product-dialog-error-categoryRequired">
                    {t('admin.products.publish.errors.categoryRequired')}
                  </p>
                  <Link
                    to={hasCategories ? `/products/${productId}/edit` : '/categories'}
                    data-testid="publish-product-dialog-categoryRequired-cta"
                    className="text-zaatar hover:text-zaatar-deep mt-2 inline-block text-start font-medium underline"
                  >
                    {t(
                      hasCategories
                        ? 'admin.products.publish.disabled.categoryRequiredCta'
                        : 'admin.products.publish.disabled.categoryRequiredCreateCta',
                    )}
                  </Link>
                </>
              ) : errorKey === 'admin.products.publish.errors.permissionDenied' ? (
                <p data-testid="publish-product-dialog-error-permissionDenied">
                  {t('admin.products.publish.errors.permissionDenied')}
                </p>
              ) : errorKey === 'admin.products.publish.errors.notFound' ? (
                <p data-testid="publish-product-dialog-error-notFound">
                  {t('admin.products.publish.errors.notFound')}
                </p>
              ) : errorKey === 'admin.products.publish.errors.networkFailed' ? (
                <p data-testid="publish-product-dialog-error-networkFailed">
                  {t('admin.products.publish.errors.networkFailed')}
                </p>
              ) : errorKey === 'admin.entitlements.productLimitBlocked' ? (
                <p data-testid="publish-product-dialog-error-productLimitBlocked">
                  {t('admin.entitlements.productLimitBlocked')}
                </p>
              ) : errorKey === 'admin.entitlements.upgradePrompt.description' ? (
                <p data-testid="publish-product-dialog-error-subscriptionRequired">
                  {t('admin.entitlements.upgradePrompt.description')}
                </p>
              ) : (
                <p data-testid="publish-product-dialog-error-unknown">
                  {t('admin.products.publish.errors.unknown')}
                </p>
              )}
            </div>
          </div>
        ) : null}

        <AlertDialogFooter className="gap-2">
          <Button
            type="button"
            variant="ghost"
            disabled={isPending}
            onClick={() => handleOpenChange(false)}
            data-testid="publish-product-dialog-cancel"
            className="text-sand-600 hover:bg-sand-100 hover:text-sand-700"
          >
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            variant="default"
            disabled={isPending}
            onClick={handleConfirm}
            data-testid="publish-product-dialog-confirm"
            aria-busy={isPending || undefined}
          >
            {isPending ? (
              <>
                <Loader2 aria-hidden="true" className="size-4 animate-spin" strokeWidth={1.75} />
                <span>{t('admin.products.publish.buttons.publishing')}</span>
              </>
            ) : (
              <>
                <Globe aria-hidden="true" className="size-4" strokeWidth={1.75} />
                <span>{t('admin.products.publish.publishModal.confirm')}</span>
              </>
            )}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
